import { IBaseModel, IOptionType } from '@zz2/zz2-ui';
import { IField } from './masterData/FARM/field/field';
import { ITrip } from './trip/trip';
import { IAccount } from './masterData/account/account';
import { IEmployee } from './masterData/auth/employee/employee';
import { ILocationLine } from './masterData/locationLines/locationLine/locationLine';
import { IOrder } from './orders/order/order';
import { ArrayHelper } from '@zz2/zz2-ts-utilities';
import lodash from 'lodash';
import { IFleet } from './masterData/fleet/fleet';
import { ILocation } from './masterData/location/location';
import { IProcess } from './masterData/process/process';
import { IDispatch } from './dispatches/dispatch/dispatch';

interface ILabelSection {
    order : number;
    section : string;
}

export default class OptionType {
    public static fromDataModel = function<T extends IBaseModel>(dataModel : T) : IOptionType {
        let labelSections : Array<ILabelSection> = [];

        if ('code' in dataModel && typeof dataModel.code === 'string' && dataModel.code !== '') {
            const labelSection : ILabelSection = {
                order: 1,
                section: dataModel.code.trim(),
            };

            labelSections = ArrayHelper.addElement(labelSections, labelSection, 'end');
        }

        if ('name' in dataModel && typeof dataModel.name === 'string' && dataModel.name !== '') {
            const labelSection : ILabelSection = {
                order: 2,
                section: dataModel.name.trim(),
            };
            labelSections = ArrayHelper.addElement(labelSections, labelSection, 'end');
        }

        if ('description' in dataModel
            && typeof dataModel.description === 'string'
            && dataModel.description !== ''
            && labelSections.length < 2) { // We don't want to add a description if the code and name have already been added.
            const labelSection : ILabelSection = {
                order: 3,
                section: dataModel.description.trim(),
            };
            labelSections = ArrayHelper.addElement(labelSections, labelSection, 'end');
        }

        if (labelSections.length < 1) {
            return {
                label: 'Label Not Found',
                value: dataModel.id,
            };
        }

        if (labelSections.length > 1) {
            const orderedList = lodash.orderBy(labelSections, x => x.order, 'asc');
            return {
                label: `(${orderedList[0].section}) - ${orderedList[1].section}`,
                value: dataModel.id,
            };
        } else {
            return {
                label: labelSections[0].section,
                value: dataModel.id,
            };
        }
    }

    /*================================================================================================================
*                                                 Custom Option Type Map
     * ==============================================================================================================*/

    public static getEnumOptions = <T extends Record<string, string | number>>(enumObj : T) : Array<IOptionType> => {
        return Object.keys(enumObj)
            .filter(key => isNaN(Number(key)))
            .map(key => ({
                label: key.replace(/([A-Z])/g, ' $1').trim(),
                value: enumObj[key as keyof T] as string | number,
            }));
    };

    /*============================================= Auth Data =====================================================*/

    public static fromEmployee = (employee : IEmployee) : IOptionType => {
        return {
            label: `(${employee.number}) - ${employee.name} ${employee.surname}`,
            value: employee.id,
        };
    };

    /*============================================= Auth Data =====================================================*/

    public static fromDispatch = (dispatch : IDispatch) : IOptionType => {
        return {
            label: `${dispatch.number}`,
            value: dispatch.id,
        };
    };

    /*============================================= Master Data =====================================================*/

    public static fromAccount = (account : IAccount) : IOptionType => {
        return {
            label: `(${account.number}) - ${account.name}`,
            value: account.id,
        };
    };

    public static fromField = (field : IField) : IOptionType => {
        return {
            label: `(${field.department?.code}) - ${field.name}`,
            value: field.id,
        };
    };

    public static fromLocation = (location : ILocation) : IOptionType => {
        return {
            label: `(${location.nursery?.name}) - ${location.name}`,
            value: location.id,
        };
    };

    public static fromLocationLine = (locationLine : ILocationLine) : IOptionType => {
        return {
            label: `(${locationLine.location?.name}) - ${locationLine.name}`,
            value: locationLine.id,
        };
    };

    public static fromFleet = (fleet : IFleet) : IOptionType => {
        return {
            label: `(${fleet.number}) - ${fleet.description}`,
            value: fleet.id,
        };
    };

    public static fromProcess = (process : IProcess) : IOptionType => {
        return {
            label: `${process.order} - ${process.name}`,
            value: process.id,
        };
    };

    /*============================================= Order =====================================================*/

    public static fromOrder = (order : IOrder) : IOptionType => {
        return {
            label: order.number,
            value: order.id,
        };
    };

    /*============================================= Trip =====================================================*/

    public static fromTrip = (trip : ITrip) : IOptionType => {
        return {
            label: `( ${trip.number} ) ${trip.fromNursery?.name}`,
            value: trip.id,
        };
    };
}